import type { ColumnDef } from "@tanstack/react-table";

import type { BaseRowItem } from "../../components/data-table";
import type { AdminSpreadSwapStatus } from "../../lib/api/adminSpreadSummaries";
import type { AdminSpreadTransaction } from "../../lib/api/adminTransactionsData";
import { formatAmount } from "../../utils/FormatPerson";

export const FEES_TABLE_LIMIT = 25;

export type FeesSwapStatusFilterValue = "all" | AdminSpreadSwapStatus;

export const FEES_SWAP_STATUS_FILTERS: {
  value: FeesSwapStatusFilterValue;
  label: string;
}[] = [
  { value: "all", label: "All" },
  { value: "PENDING", label: "Pending" },
  { value: "PROCESSING", label: "Processing" },
  { value: "SWAPPED", label: "Swapped" },
  { value: "FAILED", label: "Failed" },
];

export type FeesTableRow = BaseRowItem & {
  raw: AdminSpreadTransaction;
  displayDate: string;
  userName: string;
  userEmail: string;
  flowLabel: string;
  tradeLabel: string;
  spreadLabel: string;
  trxLabel: string;
  swapStatusLabel: string;
  transactionRef: string;
};

function num(v: unknown): number | undefined {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

export function formatFeesDate(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

const FLOW_LABELS: Record<string, string> = {
  swap: "Swap",
  sell: "Sell",
  "direct-sell": "Direct sell",
};

/** `swap` → Swap, `direct-sell` → Direct sell; unknown keys are title-cased. */
export function feesFlowLabel(flow: string | undefined): string {
  if (!flow || flow === "undefined") return "Unknown";
  if (FLOW_LABELS[flow]) return FLOW_LABELS[flow];
  const spaced = flow.replace(/[-_]/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function feesFlowBadgeClass(flow: string | undefined): string {
  switch (flow) {
    case "swap":
      return "bg-violet-500/15 text-violet-300 border-violet-500/35";
    case "sell":
      return "bg-orange-500/15 text-orange-300 border-orange-500/35";
    case "direct-sell":
      return "bg-sky-500/15 text-sky-300 border-sky-500/35";
    default:
      return "bg-[#2C363E] text-[#AEBCC6] border-[#3A4852]";
  }
}

/** TRX swap status (PENDING / PROCESSING / SWAPPED / FAILED). */
export function swapStatusBadgeClass(status: string | undefined): string {
  switch ((status ?? "").toUpperCase()) {
    case "SWAPPED":
      return "bg-emerald-500/15 text-emerald-400 border-emerald-500/35";
    case "FAILED":
      return "bg-red-500/15 text-red-300 border-red-500/35";
    case "PROCESSING":
      return "bg-sky-500/15 text-sky-300 border-sky-500/35";
    case "PENDING":
      return "bg-amber-500/15 text-amber-300 border-amber-500/35";
    default:
      return "bg-[#2C363E] text-[#AEBCC6] border-[#3A4852]";
  }
}

export function feesUserName(tx: AdminSpreadTransaction): string {
  const f = tx.user?.profile?.firstName?.trim() ?? "";
  const l = tx.user?.profile?.lastName?.trim() ?? "";
  return `${f} ${l}`.trim() || "—";
}

export function feesUserEmail(tx: AdminSpreadTransaction): string {
  return tx.user?.email?.address?.trim() || "—";
}

function shortId(id: string): string {
  return id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id || "—";
}

export function buildFeesTableRows(
  transactions: AdminSpreadTransaction[]
): FeesTableRow[] {
  return transactions.map((tx) => {
    const amount = num(tx.amount);
    const asset = tx.asset?.trim() ?? "";
    const spread = num(tx.spreadUSDT);
    const trx = num(tx.spreadSwap?.trxAmount);
    const status = tx.spreadSwap?.status?.trim() ?? "";

    return {
      id: tx._id,
      raw: tx,
      displayDate: formatFeesDate(tx.createdAt),
      userName: feesUserName(tx),
      userEmail: feesUserEmail(tx),
      flowLabel: feesFlowLabel(tx.flow),
      tradeLabel:
        amount != null
          ? `${formatAmount(amount)}${asset ? ` ${asset}` : ""}`
          : asset || "—",
      spreadLabel: spread != null ? `${formatAmount(spread)} USDT` : "—",
      trxLabel: trx != null && trx > 0 ? `${formatAmount(trx)} TRX` : "—",
      swapStatusLabel: status || "—",
      transactionRef: shortId(tx.transactionId ?? ""),
    };
  });
}

export function createFeesTableColumns(
  onOpen: (row: FeesTableRow) => void
): ColumnDef<FeesTableRow, unknown>[] {
  return [
    {
      accessorKey: "displayDate",
      header: "Date",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-adminTable-muted">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "userName",
      header: "User",
      meta: { hideBelow: "sm" as const },
      cell: ({ row }) => (
        <div className="min-w-0">
          <span className="block text-[13px] text-white">
            {row.original.userName}
          </span>
          <span className="mt-0.5 block max-w-[200px] truncate text-[11px] text-[#869AA9] sm:max-w-[240px]">
            {row.original.userEmail}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "flowLabel",
      header: "Flow",
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${feesFlowBadgeClass(
            row.original.raw.flow
          )}`}
        >
          {row.original.flowLabel}
        </span>
      ),
    },
    {
      accessorKey: "tradeLabel",
      header: "Trade",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="tabular-nums text-[13px] text-[#E7EBEE]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "spreadLabel",
      header: "Fee (USDT)",
      cell: ({ getValue }) => (
        <span className="tabular-nums text-[13px] font-semibold text-[#BEE1FB]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "trxLabel",
      header: "TRX received",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="tabular-nums text-[13px] text-[#E7EBEE]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "transactionRef",
      header: "Trade ID",
      meta: { hideBelow: "lg" as const },
      cell: ({ row }) => (
        <span
          className="font-mono text-[11px] text-[#869AA9]"
          title={row.original.raw.transactionId ?? ""}
        >
          {row.original.transactionRef}
        </span>
      ),
    },
    {
      accessorKey: "swapStatusLabel",
      header: "TRX swap",
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${swapStatusBadgeClass(
            row.original.raw.spreadSwap?.status
          )}`}
        >
          {row.original.swapStatusLabel}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { align: "right" as const },
      cell: ({ row }) => (
        <button
          type="button"
          className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-secondary_button transition hover:border-secondary_button hover:bg-[#273037]"
          onClick={() => onOpen(row.original)}
        >
          View
        </button>
      ),
    },
  ];
}
